import { Box } from 'designSystem/components/Box'
import { Button } from 'designSystem/components/Button'
import type { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import type { Highlight } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import { DocumentHeaderTitle } from 'features/knowledgeDocumentViewer/components/toolbar/DocumentHeaderTitle'
import { ExternalLink, X } from 'lucide-react'
import { keyframes, styled } from 'styled-components'

import { DocumentPreviewContent } from './DocumentPreviewContent'

const pulse = keyframes`
  50% { opacity: 0.5; }
`

const SkeletonBase = styled.div`
  animation: ${pulse} 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  border-radius: 0.25rem;
  background-color: var(--muted);
`

const SkeletonTitle = styled(SkeletonBase)`
  height: 1rem;
  width: 60%;
`

const SkeletonThumbnail = styled(SkeletonBase)`
  aspect-ratio: 1 / 1;
  width: 100%;
  border-radius: 0.25rem;
`

const SkeletonMetaLabel = styled(SkeletonBase)`
  height: 0.625rem;
  width: 40%;
`

const SkeletonMetaValue = styled(SkeletonBase)`
  height: 0.75rem;
  width: 75%;
`

const PreviewPanelTitle = styled.div`
  min-width: 0;
  color: ${(props) => props.theme.forecast.activeText};
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.375rem;
`

function PreviewActionGrid(props: React.ComponentProps<typeof Box>) {
  return (
    <Box display='grid' gridColumns='two' gap='sm' width='full' {...props} />
  )
}

export interface DocumentPreviewPanelProps {
  document: DocumentTableRow | null
  isOpen: boolean
  isLinkedDocsMode: boolean
  isLoading?: boolean
  onClose: () => void
  onDocumentNavigate: (
    doc: DocumentTableRow,
    openInNewTab: boolean,
    isLinkedDocsMode: boolean
  ) => void
  onAddToContext?: (doc: DocumentTableRow) => void
  highlights?: Highlight[] | null
  relevanceScore?: number
  detailScores?: Record<string, number>
  showIndividualScores?: boolean
  onFragmentClick?: (pageNum: number, fragmentHtml: string) => void
}

export function DocumentPreviewPanel({
  document,
  isOpen,
  isLoading = false,
  onClose,
  onDocumentNavigate,
  highlights = null,
  relevanceScore,
  detailScores,
  isLinkedDocsMode,
  showIndividualScores = true,
  onFragmentClick,
}: DocumentPreviewPanelProps) {
  const handleCloseClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    onClose()
  }

  if (!isOpen) {
    return null
  }

  if (isLoading || !document) {
    return (
      <Box
        display='flex'
        direction='column'
        height='full'
        minHeight='none'
        width='full'
        minWidth='none'
        border='left'
        surface='transparent'
        overflow='hidden'>
        <Box
          display='flex'
          align='start'
          justify='between'
          gap='xs'
          shrink={false}
          paddingX='lg'
          paddingY='sm'>
          <PreviewPanelTitle>
            <SkeletonTitle />
          </PreviewPanelTitle>
          <Box shrink={false}>
            <Button
              type='button'
              variant='ghost'
              size='icon-lg'
              onClick={handleCloseClick}
              aria-label='Close preview'>
              <X />
            </Button>
          </Box>
        </Box>
        <Box
          display='flex'
          direction='column'
          flex='one'
          minHeight='none'
          overflow='hidden'>
          <SkeletonThumbnail />
          <Box display='grid' gridColumns='two' gap='md' paddingX='lg'>
            {Array.from({ length: 4 }, (_, i) => (
              <Box key={i} display='flex' direction='column' gap='xsPlus'>
                <SkeletonMetaLabel />
                <SkeletonMetaValue />
              </Box>
            ))}
          </Box>
        </Box>
        <Box
          display='flex'
          direction='column'
          align='center'
          gap='sm'
          shrink={false}
          padding='lg'
          border='top'
          surface='muted'>
          <PreviewActionGrid>
            <Button variant='primaryRaised' size='lg' disabled>
              Open
            </Button>
            <Button
              variant='inputRaised'
              size='lg'
              disabled
              title='Open in new tab'>
              Open In New Tab
              <ExternalLink />
            </Button>
          </PreviewActionGrid>
          {/* Temporarily hidden: Add to Agent Context button
          {onAddToContext && (
            <Button variant='ghost' size='lg' width='full' disabled>
              <Plus />
              Add to Agent Context
            </Button>
          )}
          */}
        </Box>
      </Box>
    )
  }

  return (
    <Box
      display='flex'
      direction='column'
      height='full'
      minHeight='none'
      width='full'
      minWidth='none'
      border='left'
      surface='transparent'
      overflow='hidden'>
      <Box
        display='flex'
        align='start'
        justify='between'
        gap='xs'
        shrink={false}
        paddingX='lg'
        paddingY='sm'>
        <DocumentHeaderTitle documentId={document.id} />
        <Box shrink={false}>
          <Button
            type='button'
            variant='ghost'
            size='icon-lg'
            onClick={handleCloseClick}
            aria-label='Close preview'>
            <X />
          </Button>
        </Box>
      </Box>

      <Box
        display='flex'
        direction='column'
        flex='one'
        minHeight='none'
        overflow='hidden'>
        <DocumentPreviewContent
          document={document}
          showSummary
          showKeywords
          showHighlights
          showRelevanceBar
          highlights={highlights ?? undefined}
          relevanceScore={relevanceScore}
          detailScores={detailScores}
          showIndividualScores={showIndividualScores}
          onFragmentClick={onFragmentClick}
        />
      </Box>

      <Box
        display='flex'
        direction='column'
        align='center'
        gap='sm'
        shrink={false}
        padding='lg'
        border='top'
        surface='muted'>
        <PreviewActionGrid>
          <Button
            variant='primaryRaised'
            size='lg'
            onClick={() =>
              onDocumentNavigate(document, false, isLinkedDocsMode)
            }>
            Open
          </Button>
          <Button
            variant='inputRaised'
            size='lg'
            onClick={() => onDocumentNavigate(document, true, isLinkedDocsMode)}
            title='Open in new tab'>
            Open In New Tab
            <ExternalLink />
          </Button>
        </PreviewActionGrid>
        {/* Temporarily hidden: Add to Agent Context button
        {onAddToContext && (
          <Button
            variant='ghost'
            size='lg'
            width='full'
            onClick={() => onAddToContext(document)}>
            <Plus />
            Add to Agent Context
          </Button>
        )}
        */}
      </Box>
    </Box>
  )
}
